import { GridCellModes } from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { focusCell } from "../HyroTable.utils";

const useInlineEditing = ({ apiRef, columns, getCellValidationError }) => {
  const [cellModesModel, setCellModesModel] = useState({});
  const [currentEditableField, setCurrentEditableField] = useState(null);

  const editableColumns = columns.filter(column => column.editable);

  const handleCellKeyDown = useCallback((params, event) => {
    if (event.key === "Tab") {
      event.preventDefault();

      if (Object.keys(cellModesModel).length === 0) return;

      const currentColumnIndex = editableColumns.findIndex(column => column.field === params.field);
      const nextEditableColumn = event.shiftKey ? editableColumns[currentColumnIndex - 1] : editableColumns[currentColumnIndex + 1];

      if (!nextEditableColumn) return;

      setTimeout(() => {
        setCellModesModel({ [params.id]: { [nextEditableColumn.field]: { mode: GridCellModes.Edit } } });
      });
    }
  }, [cellModesModel, editableColumns, setCellModesModel]);

  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable) {
      return;
    }

    // Ignore clicks from components outside the cell
    if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) {
      return;
    }

    setCellModesModel((prevModel) => ({
      // Revert the mode of the other cells from other rows
      ...Object.keys(prevModel).reduce(
        (acc, id) => ({
          ...acc,
          [id]: Object.keys(prevModel[id] || {}).reduce(
            (acc2, field) => ({
              ...acc2,
              [field]: { mode: GridCellModes.View }
            }),
            {}
          )
        }),
        {}
      ),
      [params.id]: {
        // Revert the mode of other cells in the same row
        ...Object.keys(prevModel[params.id] || {}).reduce(
          (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
          {}
        ),
        [params.field]: { mode: GridCellModes.Edit }
      }
    }));

    if (apiRef && apiRef.current) {
      focusCell(apiRef, params.id, params.field);
    }
  }, [apiRef]);

  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleCellEditStop = useCallback((params, event) => {
    if (getCellValidationError && params.reason !== "escapeKeyDown") {
      const column = columns.find((col) => col.field === params.field);
      const editedRow = apiRef?.current?.getRowWithUpdatedValues?.(params.id, params.field);
      const value = editedRow ? editedRow[params.field] : undefined;
      if (getCellValidationError(column, value)) {
        event.defaultMuiPrevented = true;
        return;
      }
    }
    setCurrentEditableField(params.field);
  }, [getCellValidationError, columns, apiRef]);

  return {
    cellModesModel,
    handleCellClick,
    handleCellKeyDown,
    handleCellModesModelChange,
    handleCellEditStop,
    currentEditableField
  };
};

export default useInlineEditing;
